import React, {createContext, useCallback, useContext, useMemo, useRef, useState} from "react";
import Icon from "./Icon";

const ToastContext = createContext({push: () => null, dismiss: () => {}});

export const useToasts = () => useContext(ToastContext);

let nextId = 1;

// Toasts are small cards in the bottom right corner. They can carry action buttons
// (accept a challenge, open a chat...). A toast with `duration: null` stays until dismissed.
export const ToastProvider = ({children}) => {
    const [toasts, setToasts] = useState([]);
    const timers = useRef(new Map());

    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
    }, []);

    const push = useCallback((toast) => {
        const id = toast.id || `toast-${nextId++}`;
        const duration = toast.duration === undefined ? 5000 : toast.duration;
        setToasts((prev) => [...prev.filter((t) => t.id !== id), {...toast, id}].slice(-5));
        if (timers.current.has(id)) clearTimeout(timers.current.get(id));
        if (duration) {
            timers.current.set(id, setTimeout(() => dismiss(id), duration));
        }
        return id;
    }, [dismiss]);

    const value = useMemo(() => ({push, dismiss}), [push, dismiss]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div className="toast-stack" aria-live="polite">
                {toasts.map((toast) => (
                    <div key={toast.id} className={`toast ${toast.tone ? `toast--${toast.tone}` : ""}`} role="status">
                        {toast.icon}
                        <div className="toast__body">
                            {toast.title && <div className="toast__title">{toast.title}</div>}
                            {toast.text && <div className="toast__text">{toast.text}</div>}
                            {toast.actions && toast.actions.length > 0 && (
                                <div className="toast__actions">
                                    {toast.actions.map((action) => (
                                        <button
                                            key={action.label}
                                            className={`btn btn-sm ${action.variant ? `btn-${action.variant}` : ""}`}
                                            onClick={() => {
                                                action.onClick && action.onClick();
                                                if (action.keepOpen !== true) dismiss(toast.id);
                                            }}
                                        >
                                            {action.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {toast.dismissible !== false && (
                            <button className="icon-btn icon-btn--sm toast__close" aria-label="Dismiss"
                                    onClick={() => {
                                        toast.onDismiss && toast.onDismiss();
                                        dismiss(toast.id);
                                    }}>
                                <Icon name="close"/>
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
